'use client';

import { useState } from 'react';
import { X, Star, ShoppingBag, Shield, Truck, Award, Check, Plus, Minus, ChevronRight } from 'lucide-react';
import { Product } from '@/lib/products';
import { getReviewsByProduct, getAverageRating, getReviewCount } from '@/lib/reviews';
import { useCart } from '@/lib/cart-store';

interface Props {
  product: Product | null;
  onClose: () => void;
}

export default function ProductModal({ product, onClose }: Props) {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'reviews' | 'shipping'>('details');
  const { addItem, openCart } = useCart();

  if (!product) return null;

  const reviews = getReviewsByProduct(product.id);
  const avgRating = getAverageRating(product.id);
  const reviewCount = getReviewCount(product.id);

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) {
      addItem({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        category: product.category,
      });
    }
    setAdded(true);
    setTimeout(() => {
      setAdded(false);
      onClose();
      openCart();
    }, 1200);
  };

  return (
    <div
      className="fixed inset-0 z-[105] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative bg-[#0a0a0f] border border-[#D4AF37]/30 max-w-5xl w-full my-auto max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center border border-[#D4AF37]/40 bg-[#0a0a0f]/80 backdrop-blur-sm text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-all"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        <div className="grid lg:grid-cols-2 gap-0">
          {/* Left — Image */}
          <div className="relative aspect-[4/5] lg:aspect-auto lg:h-full overflow-hidden bg-[#12121a]">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
              style={{ filter: 'brightness(0.92) saturate(1.08)' }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at center, transparent 30%, rgba(10,10,15,0.6) 100%)',
              }}
            />
            {/* Tag */}
            {product.tag && (
              <div className="absolute top-5 left-5 bg-gold-gradient text-[#0a0a0f] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">
                {product.tag}
              </div>
            )}
            {/* Category */}
            <div className="absolute bottom-5 left-5 right-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/90 mb-1">
                {product.category}
              </div>
            </div>
          </div>

          {/* Right — Info */}
          <div className="p-6 sm:p-8 lg:p-10 flex flex-col">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/50 mb-4">
              <span>Boutique</span>
              <ChevronRight size={10} />
              <span>{product.category}</span>
              <ChevronRight size={10} />
              <span className="text-[#D4AF37]">{product.name}</span>
            </div>

            {/* Name + rating */}
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#F4E8C1] mb-3 leading-tight">
              {product.name}
            </h2>

            <div className="flex items-center gap-3 mb-5">
              {/* Stars */}
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={14}
                    className={
                      star <= Math.round(avgRating)
                        ? 'text-[#D4AF37] fill-[#D4AF37]'
                        : 'text-[#D4AF37]/25'
                    }
                  />
                ))}
              </div>
              <span className="text-xs text-[#F4E8C1]/70 font-medium">
                {avgRating > 0 ? avgRating.toFixed(1) : 'Nouveau'}
              </span>
              <span className="text-xs text-[#F4E8C1]/45">
                ({reviewCount} {reviewCount === 1 ? 'avis' : 'avis'})
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-[#D4AF37]/15">
              <span className="font-display text-3xl font-bold text-gold-gradient">
                {product.price.toLocaleString('fr-BE')} €
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#F4E8C1]/55">
                TVAC · Livraison calculée au checkout
              </span>
            </div>

            {/* Description */}
            <p className="text-sm text-[#F4E8C1]/75 leading-relaxed font-light mb-6">
              {product.description}
            </p>

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-3 mb-6 pb-6 border-b border-[#D4AF37]/15">
              {[
                { icon: Shield, label: 'Garantie à vie' },
                { icon: Award, label: 'Numérotée' },
                { icon: Truck, label: 'Livraison 24-48h' },
              ].map((b) => (
                <div key={b.label} className="flex flex-col items-center text-center gap-2">
                  <b.icon size={18} className="text-[#D4AF37]" />
                  <span className="text-[9px] uppercase tracking-[0.2em] text-[#F4E8C1]/65 font-medium">
                    {b.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Quantity + Add to cart */}
            <div className="flex items-center gap-3 mb-4">
              {/* Quantity */}
              <div className="flex items-center border border-[#D4AF37]/30">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-11 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-colors"
                  aria-label="Réduire"
                >
                  <Minus size={14} />
                </button>
                <span className="w-10 text-center text-sm text-[#F4E8C1] font-semibold">
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => q + 1)}
                  className="w-10 h-11 flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0f] transition-colors"
                  aria-label="Augmenter"
                >
                  <Plus size={14} />
                </button>
              </div>

              {/* Add */}
              <button
                onClick={handleAdd}
                className={`flex-1 inline-flex items-center justify-center gap-2 py-3.5 font-semibold uppercase tracking-[0.18em] text-xs transition-all duration-300 ${
                  added
                    ? 'bg-[#D4AF37] text-[#0a0a0f]'
                    : 'bg-gold-gradient text-[#0a0a0f] hover:glow-gold'
                }`}
              >
                {added ? (
                  <>
                    <Check size={14} />
                    Ajouté au panier
                  </>
                ) : (
                  <>
                    <ShoppingBag size={14} />
                    Ajouter — {(product.price * qty).toLocaleString('fr-BE')} €
                  </>
                )}
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-2 border-t border-[#D4AF37]/15 pt-5">
              <div className="flex gap-1 mb-4">
                {[
                  { id: 'details' as const, label: 'Détails' },
                  { id: 'reviews' as const, label: `Avis (${reviewCount})` },
                  { id: 'shipping' as const, label: 'Livraison' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold transition-all border-b-2 ${
                      activeTab === tab.id
                        ? 'text-[#D4AF37] border-[#D4AF37]'
                        : 'text-[#F4E8C1]/55 border-transparent hover:text-[#F4E8C1]'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              {activeTab === 'details' && (
                <div className="space-y-3 text-xs text-[#F4E8C1]/70 leading-relaxed font-light">
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Matière :</strong>{' '}
                    Cuir pleine fleur tanné végétal, sélection maison.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Façonnage :</strong>{' '}
                    Couture sellier à la main, fil de lin ciré, 7 points/cm.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Finition :</strong>{' '}
                    Patine à la cire d'abeille, 7 couches, polissage main.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Hardware :</strong>{' '}
                    Laiton doré antique, gravure laser du numéro unique.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Origine :</strong>{' '}
                    Façonné à la main à l'atelier d'Anvers, Belgique.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Certification :</strong>{' '}
                    Numérotée, certificat d'authenticité, QR blockchain.
                  </p>
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <p className="text-xs text-[#F4E8C1]/55 italic font-serif text-center py-6">
                      Cette pièce n'a pas encore d'avis. Soyez le premier à la
                      porter et à témoigner.
                    </p>
                  ) : (
                    <>
                      {/* Average */}
                      <div className="bg-[#12121a] border border-[#D4AF37]/15 p-4 mb-3">
                        <div className="flex items-center gap-4">
                          <div className="text-3xl font-display font-bold text-gold-gradient">
                            {avgRating.toFixed(1)}
                          </div>
                          <div>
                            <div className="flex items-center gap-0.5 mb-1">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star
                                  key={s}
                                  size={12}
                                  className={s <= Math.round(avgRating) ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#D4AF37]/25'}
                                />
                              ))}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/55">
                              {reviewCount} {reviewCount === 1 ? 'avis vérifié' : 'avis vérifiés'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Reviews list */}
                      {reviews.map((r) => (
                        <div key={r.id} className="border-l-2 border-[#D4AF37]/30 pl-4 py-2">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <Star
                                  key={s}
                                  size={10}
                                  className={s <= r.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#D4AF37]/25'}
                                />
                              ))}
                            </div>
                            <span className="text-[10px] text-[#D4AF37]/85 uppercase tracking-[0.15em] flex items-center gap-1">
                              <Check size={10} />
                              Achat vérifié
                            </span>
                          </div>
                          <div className="font-display text-sm font-semibold text-[#F4E8C1] mb-1">
                            {r.title}
                          </div>
                          <p className="text-xs text-[#F4E8C1]/70 leading-relaxed font-light mb-2">
                            {r.text}
                          </p>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[#F4E8C1]/45">
                            {r.author} · {r.location} ·{' '}
                            {new Date(r.date).toLocaleDateString('fr-BE', { year: 'numeric', month: 'long' })}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {activeTab === 'shipping' && (
                <div className="space-y-3 text-xs text-[#F4E8C1]/70 leading-relaxed font-light">
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Retrait boutique :</strong>{' '}
                    Gratuit, Rue Nationale 14, 2000 Anvers. Sous 24h ouvrées.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Belgique :</strong>{' '}
                    15 € — Offerte dès 500 €. Délai 24-48h ouvrées.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Europe :</strong>{' '}
                    35 €. Délai 3-5 jours ouvrés.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Monde :</strong>{' '}
                    65 €. Délai 5-10 jours ouvrés.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Retours :</strong>{' '}
                    30 jours pour échange ou remboursement. Pièce non portée,
                    dans son écrin d'origine.
                  </p>
                  <p>
                    <strong className="text-[#D4AF37] not-italic">Garantie :</strong>{' '}
                    À vie. Réparation atelier à perpétuité. Pièce transmissible.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
